import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState, type JSX, type ReactNode } from 'react';

import { Card } from '@/components/ui/card.js';
import type {
  CaseOutcome,
  ErrorDigestFailed,
  ReportErrorDigest,
  ResultStatus,
} from '@/lib/api/quality-types.js';
import { CASE_OUTCOMES } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import { StatusBadge } from '../quality-presentation.js';
import { caseDisplayStatus, outcomeLabel } from '../quality-outcome.js';
import { EventList } from './EventList.js';

function hasCanonicalResults(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  const keys = Object.keys(row);
  const required = CASE_OUTCOMES.filter((outcome) => outcome !== 'unresolved');
  return (
    (keys.length === required.length || keys.length === CASE_OUTCOMES.length) &&
    keys.every((key) => CASE_OUTCOMES.includes(key as CaseOutcome)) &&
    [
      ...required,
      ...(Object.hasOwn(row, 'unresolved') ? ['unresolved'] : []),
    ].every((outcome) => {
      const count = row[outcome];
      return typeof count === 'number' && Number.isInteger(count) && count >= 0;
    })
  );
}

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry),
    )
  );
}

/**
 * The server buckets by presented status, so a newer digest also carries `assessing`, and a
 * repeated run's skipped iterations (MEDA-1288) carry `not_run`.
 */
const DIGEST_BUCKETS = [...CASE_OUTCOMES, 'assessing', 'not_run'] as const;

type DigestBucket = (typeof DIGEST_BUCKETS)[number];

/**
 * Entries regrouped by the status a person sees, so an older service's verdict buckets never show
 * an unconfirmed or not yet judged case as a verdict. An entry without an outcome keeps its bucket.
 */
function groupByShownStatus(
  report: ReportErrorDigest,
): Record<DigestBucket, ErrorDigestFailed[]> {
  const groups = Object.fromEntries(
    DIGEST_BUCKETS.map((bucket) => [bucket, []]),
  ) as unknown as Record<DigestBucket, ErrorDigestFailed[]>;
  for (const bucket of DIGEST_BUCKETS) {
    for (const item of report[bucket] ?? []) {
      const shown =
        bucket === 'assessing' || bucket === 'not_run'
          ? bucket
          : caseDisplayStatus({
              ...item,
              outcome: item.outcome ?? bucket,
              status: 'failed',
            });
      groups[
        (DIGEST_BUCKETS as readonly string[]).includes(shown)
          ? (shown as DigestBucket)
          : bucket
      ].push(item);
    }
  }
  return groups;
}

function hasCanonicalBuckets(report: ReportErrorDigest): boolean {
  return DIGEST_BUCKETS.every((outcome) => {
    const bucket: unknown = report[outcome];
    return (
      bucket === undefined ||
      (isRecordArray(bucket) &&
        bucket.every((entry) =>
          entry.last_events === undefined
            ? true
            : isRecordArray(entry.last_events),
        ))
    );
  });
}

/** Header + body wrapper for a breakdown panel — the web equivalent of the admin's SectionCard. */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        {hint ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </div>
      <div className="px-4">{children}</div>
    </Card>
  );
}

function OutcomeCard({
  tc,
  outcome,
}: {
  tc: ErrorDigestFailed;
  outcome?: CaseOutcome | 'assessing' | 'not_run';
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const events = tc.last_events ?? [];
  const hasEvents = events.length > 0;
  const borderClass = outcome
    ? outcome === 'passed'
      ? 'border-l-success-500'
      : outcome === 'assessing' || outcome === 'not_run'
        ? 'border-l-border'
        : outcome === 'defect_found' || outcome === 'qa_error'
          ? 'border-l-destructive'
          : 'border-l-warning-500'
    : tc.status === 'broken' || tc.status === 'timeout'
      ? 'border-l-warning-500'
      : 'border-l-destructive';
  return (
    <div
      className={cn(
        'rounded-lg border border-border p-3 border-l-2',
        borderClass,
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium">{tc.id}</span>
        <StatusBadge status={outcome ?? (tc.status as ResultStatus)} />
      </div>
      {tc.error ? (
        <p className="mt-1.5 text-sm text-destructive">{tc.error}</p>
      ) : null}
      {hasEvents ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {open ? (
              <ChevronDownIcon className="size-3.5" />
            ) : (
              <ChevronRightIcon className="size-3.5" />
            )}
            Last {events.length} event
            {events.length === 1 ? '' : 's'}
            {outcome ? '' : ' before failure'}
          </button>
          {open ? (
            <div className="mt-1.5 border-t border-border pt-1.5">
              <EventList events={events} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ErrorDigestReport({
  report,
}: {
  report: ReportErrorDigest;
}): JSX.Element {
  if (report.outcome_protocol_version === 1) {
    if (!hasCanonicalResults(report.results) || !hasCanonicalBuckets(report)) {
      return (
        <p className="py-3 text-sm text-destructive">Outcome unavailable.</p>
      );
    }
    const groups = groupByShownStatus(report);
    return (
      <div className="flex flex-col gap-4">
        {DIGEST_BUCKETS.filter((outcome) =>
          outcome === 'assessing' || outcome === 'not_run'
            ? groups[outcome].length > 0
            : outcome !== 'unresolved' ||
              report.schema_version === 5 ||
              report.unresolved !== undefined ||
              groups.unresolved.length > 0,
        ).map((outcome) => {
          const cases = groups[outcome];
          return (
            <Section
              key={outcome}
              title={outcomeLabel(outcome)}
              hint={String(cases.length)}
            >
              {cases.length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">No cases.</p>
              ) : (
                <div className="flex flex-col gap-2 py-3">
                  {cases.map((tc) => (
                    <OutcomeCard key={tc.id} tc={tc} outcome={outcome} />
                  ))}
                </div>
              )}
            </Section>
          );
        })}
      </div>
    );
  }
  if (report.outcome_protocol_version !== null) {
    return (
      <p className="py-3 text-sm text-destructive">Outcome unavailable.</p>
    );
  }
  if (!Array.isArray(report.failed) || !Array.isArray(report.passed)) {
    return (
      <p className="py-3 text-sm text-destructive">Outcome unavailable.</p>
    );
  }
  const failed = report.failed;
  const passed = report.passed;
  return (
    <div className="flex flex-col gap-4">
      <Section
        title="Failed / broken"
        hint={`${failed.length} of ${failed.length + passed.length}`}
      >
        {failed.length === 0 ? (
          <p className="py-4 text-center text-sm text-success-600">
            All tests passed.
          </p>
        ) : (
          <div className="flex flex-col gap-2 py-3">
            {failed.map((tc) => (
              <OutcomeCard key={tc.id} tc={tc} />
            ))}
          </div>
        )}
      </Section>

      {passed.length > 0 ? (
        <Section title="Passed" hint={`${passed.length}`}>
          <ul className="flex flex-wrap gap-1.5 py-3">
            {passed.map((tc) => (
              <li
                key={tc.id}
                className="rounded-md border border-success-500/30 bg-success-500/5 px-2 py-1 text-xs"
                title={tc.title ?? undefined}
              >
                <span className="font-mono">{tc.id}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
